import React, { useState, useEffect } from 'react';
import { Modal, Table, Spinner, Button } from 'react-bootstrap';
import { BookmarkFill, Bookmark } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { usePerformanceDayDetail } from '../../hooks/usePerformanceData';
import { useQueryClient } from '@tanstack/react-query';
import TradeJournalModal from '../TradeJournal/TradeJournalModal';

// ─── Formatters (shared with PerformanceDashboard style) ──────────────────────

const fmt$ = (v) =>
  v >= 0 ? `+$${v.toFixed(2)}` : `-$${Math.abs(v).toFixed(2)}`;

const fmtPrice = (v) => (v != null ? `$${Number(v).toFixed(2)}` : '—');

const fmtTime = (str) => {
  if (!str) return '—';
  const d = new Date(str);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const fmtDuration = (mins) => {
  if (mins == null || mins < 0) return '—';
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  return h ? `${d}d ${h}h` : `${d}d`;
};

const formatDayHeader = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
};

// ─── Reusable closed-trades table ─────────────────────────────────────────────

/**
 * ClosedTradesTable — renders a trades array in the same table style as
 * PerformanceDashboard's recent trades table.
 *
 * Props:
 *   trades         — array from /performance/calendar/day (with hasJournal, journalId, tradeId)
 *   colors         — theme colors
 *   onJournalClick — (trade) => void, called when bookmark icon / Journal button clicked
 */
export const ClosedTradesTable = ({ trades = [], colors, onJournalClick }) => (
  <Table
    bordered
    hover
    size="sm"
    style={{
      fontSize: '0.8rem',
      marginBottom: 0,
      backgroundColor: colors.bgPrimary,
      color: colors.text,
      '--bs-table-bg': colors.bgPrimary,
      '--bs-table-color': colors.text,
      '--bs-table-hover-bg': colors.bgSecondary,
      '--bs-table-hover-color': colors.text,
      '--bs-table-border-color': colors.border,
    }}
  >
    <thead>
      <tr style={{ fontSize: '0.72rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        <th style={{ color: colors.textMuted }}>Symbol</th>
        <th style={{ color: colors.textMuted }}>Side</th>
        <th style={{ color: colors.textMuted }}>Entry</th>
        <th style={{ color: colors.textMuted }}>Exit</th>
        <th style={{ color: colors.textMuted }}>Qty</th>
        <th style={{ color: colors.textMuted }}>Duration</th>
        <th style={{ textAlign: 'right', color: colors.textMuted }}>P&amp;L</th>
        {onJournalClick && <th style={{ width: 80, color: colors.textMuted }} />}
      </tr>
    </thead>
    <tbody>
      {trades.map((t, i) => {
        const hasPnl = t.pnl != null;
        const pnlColor = !hasPnl ? colors.textMuted : t.pnl >= 0 ? colors.success : colors.danger;
        const rowKey = t.tradeId || `${t.symbol}-${i}`;

        return (
          <tr key={rowKey}>
            <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
              <strong style={{ fontSize: '0.8rem', fontWeight: 700, color: colors.text }}>
                {t.symbol}
              </strong>
            </td>
            <td style={{ verticalAlign: 'middle', color: colors.text, textTransform: 'capitalize' }}>
              {t.direction || '—'}
            </td>
            <td style={{ verticalAlign: 'middle', color: colors.text, whiteSpace: 'nowrap' }}>
              <div>{fmtPrice(t.entryPrice)}</div>
              <div style={{ fontSize: '0.68rem', color: colors.textMuted }}>{fmtTime(t.entryTime)}</div>
            </td>
            <td style={{ verticalAlign: 'middle', color: colors.text, whiteSpace: 'nowrap' }}>
              <div>{fmtPrice(t.exitPrice)}</div>
              <div style={{ fontSize: '0.68rem', color: colors.textMuted }}>{fmtTime(t.exitTime)}</div>
            </td>
            <td style={{ verticalAlign: 'middle', color: colors.text }}>{t.quantity ?? '—'}</td>
            <td style={{ verticalAlign: 'middle', color: colors.text, whiteSpace: 'nowrap' }}>
              {fmtDuration(t.durationMinutes)}
            </td>
            <td style={{ verticalAlign: 'middle', textAlign: 'right', fontWeight: 700, color: pnlColor, whiteSpace: 'nowrap' }}>
              {hasPnl ? fmt$(t.pnl) : '—'}
            </td>
            {onJournalClick && (
              <td style={{ verticalAlign: 'middle', textAlign: 'center', padding: '0 8px' }}>
                {t.hasJournal ? (
                  <button
                    onClick={() => onJournalClick(t)}
                    title="View journal"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.success, padding: '2px 4px', lineHeight: 1 }}
                  >
                    <BookmarkFill size={14} />
                  </button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => onJournalClick(t)}
                    style={{
                      fontSize: '0.72rem',
                      padding: '2px 8px',
                      borderColor: colors.border,
                      color: colors.textMuted,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 3,
                    }}
                  >
                    <Bookmark size={11} />
                    Journal
                  </Button>
                )}
              </td>
            )}
          </tr>
        );
      })}
    </tbody>
  </Table>
);

// ─── JournalDayModal ───────────────────────────────────────────────────────────

/**
 * JournalDayModal — shows trades for a selected calendar day.
 * Clicking Journal closes this modal and opens TradeJournalModal; after the
 * journal is saved/dismissed this modal reopens automatically.
 */
const JournalDayModal = ({ show, onHide, dateStr, accountId, paperTrading }) => {
  const { colors } = useTheme();
  const [journalTrade, setJournalTrade] = useState(null);
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = usePerformanceDayDetail({
    dateKey: dateStr,
    accountId,
    paperTrading,
    enabled: (show || !!journalTrade) && !!dateStr,
  });

  // Refetch day detail whenever a journal is created or updated
  useEffect(() => {
    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ['performance', 'calendar-day'] });
    };
    window.addEventListener('tradeJournal:created', invalidate);
    window.addEventListener('tradeJournal:updated', invalidate);
    return () => {
      window.removeEventListener('tradeJournal:created', invalidate);
      window.removeEventListener('tradeJournal:updated', invalidate);
    };
  }, [queryClient]);

  const summary = data?.summary ?? { pnl: 0, trades: 0, winRate: 0 };
  const trades = data?.trades ?? [];

  const handleJournalClick = (trade) => {
    setJournalTrade(trade);
  };

  const handleJournalClose = () => {
    setJournalTrade(null);
  };

  const positionData = journalTrade ? {
    Symbol: journalTrade.symbol,
    Direction: journalTrade.direction,
    EntryPrice: journalTrade.entryPrice,
    ExitPrice: journalTrade.exitPrice,
    Quantity: journalTrade.quantity,
    OpenedDateTime: journalTrade.entryTime,
    ClosedDateTime: journalTrade.exitTime,
    RealizedPnL: journalTrade.pnl,
  } : null;

  // Show day modal only when journal modal is not open
  const showDayModal = show && !journalTrade;

  return (
    <>
      <Modal
        show={showDayModal}
        onHide={onHide}
        centered
        size="lg"
        scrollable
        style={{ zIndex: 10055 }}
        container={typeof document !== 'undefined' ? document.body : undefined}
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: colors.bgSecondary,
            borderBottom: `1px solid ${colors.border}`,
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Left: day title + context */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Modal.Title style={{ color: colors.text, fontSize: '1rem', fontWeight: 600, marginBottom: 0 }}>
              {formatDayHeader(dateStr)}
            </Modal.Title>
            <div style={{ color: colors.textMuted, fontSize: '0.72rem', marginTop: 2 }}>
              Trades closed today
            </div>
          </div>

          {/* Right: summary stats */}
          {summary.trades > 0 && (
            <div style={{ display: 'flex', gap: 20, marginRight: 12, flexShrink: 0 }}>
              {[
                { label: 'P&L', value: fmt$(summary.pnl), color: summary.pnl >= 0 ? colors.success : colors.danger },
                { label: 'Trades', value: summary.trades, color: colors.text },
                { label: 'Win Rate', value: `${(summary.winRate * 100).toFixed(0)}%`, color: colors.text },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.65rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 700, color, lineHeight: 1.2 }}>{value}</div>
                </div>
              ))}
            </div>
          )}
        </Modal.Header>

        <Modal.Body style={{ backgroundColor: colors.bgSecondary, padding: '0' }}>
          {isLoading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: colors.textMuted, padding: '20px 24px' }}>
              <Spinner animation="border" size="sm" style={{ color: colors.primary }} />
              <span style={{ fontSize: '0.85rem' }}>Loading trades…</span>
            </div>
          )}

          {isError && !isLoading && (
            <p style={{ color: colors.danger, fontSize: '0.875rem', padding: '20px 24px', marginBottom: 0 }}>
              Could not load trades for this day.
            </p>
          )}

          {!isLoading && !isError && trades.length === 0 && (
            <p style={{ color: colors.textMuted, fontSize: '0.875rem', padding: '20px 24px', marginBottom: 0 }}>
              No closed trades on this day.
            </p>
          )}

          {!isLoading && trades.length > 0 && (
            <ClosedTradesTable
              trades={trades}
              colors={colors}
              onJournalClick={handleJournalClick}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Journal modal — opens in place of day modal, day modal reopens on close */}
      <TradeJournalModal
        showModal={!!journalTrade}
        handleCloseModal={handleJournalClose}
        positionData={positionData}
        tradeId={journalTrade?.tradeId || null}
        isAutoOpened={true}
      />
    </>
  );
};

export default JournalDayModal;
